import { Button, Checkbox, Divider, Grid, Typography } from '@material-ui/core/';
import { makeStyles } from '@material-ui/core/styles';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import ChowbusForm from '~/components/ChowbusForm';
import PasswordInput from '~/components/ChowbusForm/PasswordInput';
import InternationalPhoneInput from '~/components/ChowbusForm/InternationalPhoneInput';
import { useAppConfig } from '~/hooks/useAppConfig';
import { userCookie } from '~/models/UserCookie';
import * as signupActions from '~/store/legacy/actions/signUpActions.js';
import { toSupportedCountryAbbreviation } from '~/utils/format';
import FacebookLoginButton from '../buttons/FacebookLoginButton';
import GoogleSignInButton from '../buttons/GoogleSignInButton.js';
import { isWeChatEnv, isAlipayEnv } from '../../utils/env.js'

const useStyles = makeStyles((theme) => {
  return {
    formContainer: {
      [theme.breakpoints.down('sm')]: {
        width: '80%',
      },
      width: '60%',
    },
    withDivider: {
      position: 'relative',
    },
    rightAngle: {
      borderRadius: 0,
    },
    orContinueWith: {
      margin: 'auto 10px',
    },
    rightBoard: {
      [theme.breakpoints.up('sm')]: {
        // borderRight: 'none',
      },
    },
    agreeToTerm: {
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
      margin: 'auto',
    },
    termLink: {
      fontSize: '0.8em',
      color: theme.palette.primary.dark,
      margin: '0 2px',
    },
    leftBoard: {
      [theme.breakpoints.up('sm')]: {
        borderLeft: 'none',
      },
    },
    signUpButton: {
      marginTop: 20,
    },
  };
});

export default SignUpForm;

function SignUpForm() {
  const [isInWebView, setIsInWebView] = useState(false);
  const dispatch = useDispatch();
  const appConfig = useAppConfig();
  const userServiceRegion =
    !appConfig.isLoading && userCookie.serviceRegionId
      ? appConfig.serviceRegions[userCookie.serviceRegionId]
      : null;

  const classes = useStyles();
  const [agreeToTerm, setAgreeToTerm] = useState(false);

  const handleChangeAgreeToTerm = (e) => {
    const agree = e.target.checked;
    setAgreeToTerm(agree);
  };

  const signUp = (params) => {
    const { email, firstName, lastName, password, phoneNumber } = params;
    dispatch(signupActions.signUp({ email, firstName, lastName, password, phoneNumber }));
  };

  const onFacebookLogin = (params) => {
    const {
      first_name: firstName,
      last_name: lastName,
      email,
      userID: providerUid,
      accessToken: providerToken,
    } = params;
    dispatch(
      signupActions.signUp({
        email,
        firstName,
        lastName,
        provider: 'facebook',
        providerUid,
        providerToken,
      })
    );
  };

  const onGoogleLogin = (params) => {
    const { email, firstName, lastName, providerUid, providerToken } = params;
    dispatch(
      signupActions.signUp({
        email,
        firstName,
        lastName,
        provider: 'google',
        providerUid,
        providerToken,
      })
    );
  };

  const onSubmit = (values) => {
    signUp({
      ...values,
      ...{
        phoneNumber: values?.phoneNumber?.number,
      },
    });
  };
  const isAlipay = appConfig.isAlipay;

  useEffect(() => {
    const isInWebView = isWeChatEnv() || isAlipayEnv();
    setIsInWebView(isInWebView);
  }, []);

  return (
    <ChowbusForm.Form name='sign-up' onSubmit={onSubmit}>
      <Typography variant='subtitle1' align='center'>
        Create Account
      </Typography>
      <Grid container>
        <Grid item xs={12} sm={6} className={classes.withDivider}>
          <ChowbusForm.Field name='firstName' required>
            <ChowbusForm.Input
              outlinedClass={classes.rightBoard}
              require
              required
              placeholder='First Name'
              autoComplete='first-name'
              fullWidth
            />
          </ChowbusForm.Field>
        </Grid>
        <Grid item xs={12} sm={6}>
          <ChowbusForm.Field name='lastName' required>
            <ChowbusForm.Input
              outlinedClass={classes.leftBoard}
              disableUnderline
              required
              placeholder='Last Name'
              fullWidth
            />
          </ChowbusForm.Field>
        </Grid>
      </Grid>
      <ChowbusForm.Field name='email' validate={ChowbusForm.validators.emailValidator} required>
        {(fieldContext) => (
          <ChowbusForm.Input
            disableUnderline
            name={fieldContext.name}
            required
            onChange={fieldContext.onChange}
            error={fieldContext.error}
            errorMessage={fieldContext.errorMessage}
            placeholder='Email'
            fullWidth
          />
        )}
      </ChowbusForm.Field>
      <ChowbusForm.Field
        name='password'
        validate={ChowbusForm.validators.passwordValidator}
        required>
        {(fieldContext) => (
          <PasswordInput
            name={fieldContext.name}
            onChange={fieldContext.onChange}
            error={fieldContext.error}
            errorMessage={fieldContext.errorMessage}
            fieldValue={fieldContext.fieldValue}
            placeholder='Password'
            isFieldDirty={fieldContext.isFieldDirty}
            shouldShowStrengthChecker
          />
        )}
      </ChowbusForm.Field>
      <ChowbusForm.Field
        name='password2'
        validate={[
          ChowbusForm.validators.passwordValidator,
          ChowbusForm.validators.matchOtherFieldValidator('password', 'Password'),
        ]}
        required>
        {(fieldContext) => (
          <PasswordInput
            name={fieldContext.name}
            onChange={fieldContext.onChange}
            error={fieldContext.error}
            errorMessage={fieldContext.errorMessage}
            fieldValue={fieldContext.fieldValue}
            placeholder='Confirm Password'
            isFieldDirty={fieldContext.isFieldDirty}
            matchWithFieldValue={fieldContext.fields['password']}
            shouldShowStrengthChecker
          />
        )}
      </ChowbusForm.Field>
      {!appConfig.isLoading ? (
        <ChowbusForm.Field
          name='phoneNumber'
          validate={ChowbusForm.validators.i18nPhoneNumberValidator}>
          {(fieldContext) => (
            <InternationalPhoneInput
              error={fieldContext.error}
              errorMessage={fieldContext.errorMessage}
              onChange={fieldContext.onChange}
              name={fieldContext.name}
              variant='signup'
              country={toSupportedCountryAbbreviation(userServiceRegion?.country || '')}
            />
          )}
        </ChowbusForm.Field>
      ) : null}
      <TermOfUser handleChangeAgreeToTerm={handleChangeAgreeToTerm} />
      <Grid container justify='center'>
        <ChowbusForm.Field>
          {(fieldContext) => (
            <Button
              className={classes.signUpButton}
              fullWidth
              variant='contained'
              color='primary'
              size='large'
              disabled={!fieldContext.isValid || !agreeToTerm}
              onClick={fieldContext.onSubmit}>
              Sign Up
            </Button>
          )}
        </ChowbusForm.Field>
      </Grid>
      {
        !isAlipay && !isInWebView && <React.Fragment>
          <Divider style={{ margin: '30px 0' }} />
          <Grid container justify='center' alignItems='center'>
            <Typography className={classes.orContinueWith}>or continue with</Typography>
            <FacebookLoginButton
              onFacebookLogin={onFacebookLogin}
              size={35}
              className={classes.orContinueWith}
              authAction='sign-up'
            />
            <GoogleSignInButton onGoogleLogin={onGoogleLogin} authAction='sign-up' />
          </Grid>
        </React.Fragment>
      }

    </ChowbusForm.Form>
  );
}

function TermOfUser(props) {
  const classes = useStyles();
  const { handleChangeAgreeToTerm } = props;
  return (
    <Grid container alignItems='center' className={classes.agreeToTerm} justify='flex-start'>
      <Grid item>
        <Checkbox
          name='agreeToTerms'
          inputProps={{ 'aria-label': 'agreeToTerms', name: 'agreeToTerms' }}
          color='primary'
          onChange={handleChangeAgreeToTerm}
        />
      </Grid>
      <Grid item sm={10} xs={10}>
        <Grid container alignItems='center'>
          <Typography
            style={{ fontSize: '0.77em', marginLeft: 3, lineHeight: '44px', letterSpacing: 0 }}>
            I agree to the
          </Typography>
          <Link href='/terms-of-use'>
            <a className={classes.termLink}>
              <Typography variant='caption' color='primary'>
                Terms of Use
              </Typography>
            </a>
          </Link>
          <Typography variant='caption'>&</Typography>
          <Link href='/privacy-policy'>
            <a className={classes.termLink}>
              <Typography variant='caption' color='primary'>
                Privacy Policy
              </Typography>
            </a>
          </Link>
        </Grid>
      </Grid>
    </Grid>
  );
}
